import React from "react";
import { Box } from "@mui/material";
import { useLocation, useNavigate } from "react-router-dom";

import { CustomBox } from "../../Shared/CustomBox/CustomBox";
import { CustomImage } from "../../Shared/CustomImage/CustomImage";
import { CustomTypography } from "../../Shared/CustomTypography/CustomTypography";
import { CustomButton } from "../../Shared/CustomButton/CustomButton";

import { DateSlot } from "./DateSlot";
import { InfoCard } from "./InfoCard";
import { InfoRow } from "./InfoRow";
import { HealthPlan } from "./HealthPlan";

import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs, { Dayjs } from "dayjs";

export const ReferralAppointment = () => {
  
  const { state } = useLocation();
  const navigate = useNavigate();

  const referral = state?.referral;

  const [selectedDate, setSelectedDate] = React.useState<Dayjs | null>(dayjs());
  const [openDatePicker, setOpenDatePicker] = React.useState(false);

  if (!referral) {
    return (
      <Box p={3}>
        <CustomTypography>No referral selected</CustomTypography>
      </Box>
    );
  }

  const { referredTo } = referral;

  const formattedDate = selectedDate
    ? selectedDate.format("dddd, MMMM D, YYYY")
    : "No date selected";

  return (
    <Box width="100%" p={2}>
      {/* Header */}
      <CustomBox alignItems="center" mb={2}>
        <CustomTypography
          fontSize={20}
          fontWeight={600}
          sx={{ cursor: "pointer" }}
          onClick={() => navigate(-1)}
        >
          ← Referral
        </CustomTypography>
      </CustomBox>

      {/* Main Layout */}
      <Box display="grid" gridTemplateColumns="2fr 1fr" gap={3}>
        {/* ================= LEFT SECTION ================= */}
        <Box
          bgcolor="#fff"
          borderRadius={3}
          p={3}
          boxShadow="0px 4px 20px rgba(0,0,0,0.06)"
        >
          <CustomTypography fontWeight={600} mb={2}>
            Schedule Appointment
          </CustomTypography>

          {/* Doctor Info */}
          <CustomBox alignItems="center" gap={2} mb={3}>
            <CustomImage
              src={referredTo.avatar}
              width={48}
              height={48}
              radius="50%"
            />
            <CustomBox flexDirection="column">
              <CustomTypography fontWeight={600}>
                {referredTo.name}
              </CustomTypography>
              <CustomTypography fontSize={13} color="gray">
                {referredTo.specialty || "Urologist"}
              </CustomTypography>
            </CustomBox>
          </CustomBox>

          {/* Available Time Slots Header */}
          <CustomBox justifyContent="space-between" mb={1}>
            <CustomTypography fontWeight={600}>
              Available Time Slots
            </CustomTypography>

            <LocalizationProvider dateAdapter={AdapterDayjs}>
              {/* Trigger Button */}
              <CustomButton
                onClick={() => setOpenDatePicker(true)}
                sx={{
                  backgroundColor: "#2196f3",
                  color: "#fff",
                  px: 2,
                  py: 0.5,
                  borderRadius: "8px",
                  fontSize: 13,
                  "&:hover": { backgroundColor: "#1976d2" },
                }}
              >
                Select Your Date
              </CustomButton>

              {/* Hidden Date Picker */}
              <DatePicker
                open={openDatePicker}
                onClose={() => setOpenDatePicker(false)}
                value={selectedDate}
                onChange={(newValue) => setSelectedDate(newValue)}
                disablePast
                slotProps={{
                  textField: {
                    //sx: {
                    //   position: "absolute",
                    //   opacity: 0,
                    //   pointerEvents: "none",
                    //   width: 0,
                    //   height: 0,
                    // },
                  },
                }}
              />
            </LocalizationProvider>
          </CustomBox>

          {/* ✅ Selected Date Visible */}
          <CustomTypography fontSize={14} color="gray" mb={2}>
            {formattedDate}
          </CustomTypography>

          {/* Time Slots */}
          <DateSlot
            date={formattedDate}
            slots={[
              { time: "09:00 AM", active: true },
              { time: "10:30 AM" },
              { time: "02:00 PM", disabled: true },
            ]}
          />

          <DateSlot
            date="Tuesday, September 2, 2025"
            slots={[
              { time: "10:00 AM" },
              { time: "11:30 AM" },
              { time: "03:00 PM" },
            ]}
          />

          <DateSlot
            date="Wednesday, September 3, 2025"
            slots={[
              { time: "09:30 AM" },
              { time: "01:00 PM", disabled: true },
            ]}
          />
        </Box>

        {/* ================= RIGHT SECTION ================= */}
        <Box>
          <CustomButton
            fullWidth
            sx={{
              backgroundColor: "#2196f3",
              color: "#fff",
              py: 1.2,
              borderRadius: "10px",
              mb: 2,
              "&:hover": { backgroundColor: "#1976d2" },
            }}
          >
            Confirm Appointment
          </CustomButton>

          {/* Selected Appointment */}
          <InfoCard title="Selected Appointment" bg="#EAF7EE">
            <InfoRow label="Date" value={formattedDate} />
            <InfoRow label="Time" value="09:00 AM" />
            <InfoRow label="Doctor" value={referredTo.name} />
          </InfoCard>

          {/* Health Plans */}
          <InfoCard title="Health Plan Accepted" bg="#F2F8FF">
            <HealthPlan name="Aetna" />
            <HealthPlan name="Anthem Blue Cross" />
            <HealthPlan name="Blue Shield of California" />
          </InfoCard>
        </Box>
      </Box>
    </Box>
  );
};
